/**
 * Scanyt dans le navigateur.
 *
 * L'interface (public/app.js de l'application locale, inchangée) appelle une
 * API en /api/… . Ici, ces appels n'atteignent aucun serveur : fetch() est
 * intercepté et la route est exécutée sur place par le même server.js, dont
 * les fichiers vivent dans IndexedDB et dont le scan passe par le tunnel.
 *
 * Ce script doit être chargé AVANT app.js, qui appelle l'API dès son chargement.
 */
import { charger, persistant } from './stockage.js';
import { traiter } from './shims/express.cjs';
import { FOURNISSEURS } from './fournisseurs.js';
import { installerTutoriel } from './tutoriel.js';
import { brancherMicrosoft, preparerScan } from './formulaire-microsoft.js';
import '../../server.js';

const pret = charger();

// Routes propres à la version web, prioritaires sur celles de server.js.
const ROUTES_WEB = {
  'GET /api/providers': () => Object.fromEntries(
    Object.entries(FOURNISSEURS).map(([cle, f]) => [cle, { label: f.label, host: f.host, port: 993, secure: true }])
  )
};

function reponseJson(objet, statut = 200) {
  return new Response(JSON.stringify(objet), {
    status: statut,
    headers: { 'Content-Type': 'application/json; charset=utf-8' }
  });
}

async function appelerApi(methode, url, corps) {
  await pret;
  const { pathname } = new URL(url, location.href);
  const web = ROUTES_WEB[`${methode} ${pathname}`];
  if (web) return reponseJson(web(corps));
  try {
    return (await traiter(methode, url, corps)) || reponseJson({ error: 'Route inconnue.' }, 404);
  } catch (err) {
    return reponseJson({ error: err.message || 'Erreur inconnue.' }, 500);
  }
}

function estApiLocale(url) {
  return url.origin === location.origin
    && url.pathname.startsWith('/api/')
    // Les logos, eux, sont cherchés par Cloudflare (functions/api/logo).
    && !url.pathname.startsWith('/api/logo/');
}

// ---------------------------------------------------------------------------
// Mémoriser le mot de passe.
//
// Scanyt ne l'enregistre jamais lui-même : c'est le gestionnaire de mots de
// passe du navigateur qui s'en charge, chiffré, et le remplit la fois
// suivante. Encore faut-il qu'il le voie passer : app.js vide le champ dès le
// début du scan. On le retient donc en mémoire le temps du scan, et on ne le
// propose au navigateur qu'une fois la connexion réussie — inutile de lui
// faire mémoriser un mot de passe faux.
// ---------------------------------------------------------------------------
let identifiantsEnAttente = null;
const CLE_DERNIERE_BOITE = 'scanyt-derniere-boite';

document.addEventListener('submit', (evenement) => {
  if (evenement.target.id !== 'scan-form') return;
  const email = document.getElementById('email').value.trim();
  const champ = document.getElementById('password');
  const fournisseur = document.getElementById('provider').value;
  // Google affiche la clé par groupes de quatre lettres : les espaces copiés
  // avec elle ne font pas partie du mot de passe.
  if (fournisseur === 'gmail') champ.value = champ.value.replace(/\s+/g, '');
  const motDePasse = champ.value;
  identifiantsEnAttente = email && motDePasse ? { email, motDePasse } : null;
  // La messagerie et l'adresse ne sont pas secrètes : on les garde pour
  // pré-remplir le formulaire la prochaine fois.
  try { localStorage.setItem(CLE_DERNIERE_BOITE, JSON.stringify({ email, fournisseur })); } catch { /* stockage refusé */ }
}, true);

async function proposerAuNavigateur() {
  const identifiants = identifiantsEnAttente;
  identifiantsEnAttente = null;
  if (!identifiants || !window.PasswordCredential || !navigator.credentials) return;
  try {
    await navigator.credentials.store(new window.PasswordCredential({
      id: identifiants.email,
      password: identifiants.motDePasse,
      name: identifiants.email
    }));
  } catch { /* refusé par l'utilisateur ou le navigateur */ }
}

function suivreScan(reponse) {
  if (!identifiantsEnAttente) return;
  reponse.clone().json().then((etat) => {
    if (etat.status === 'done') proposerAuNavigateur();
    else if (etat.status === 'error') identifiantsEnAttente = null;
  }, () => {});
}

const fetchReseau = window.fetch.bind(window);
window.fetch = async (entree, options = {}) => {
  const url = new URL(typeof entree === 'string' ? entree : entree.url, location.href);
  if (!estApiLocale(url)) return fetchReseau(entree, options);
  const methode = (options.method || 'GET').toUpperCase();
  let corps = options.body ? JSON.parse(options.body) : {};
  if (methode === 'POST' && url.pathname === '/api/scan') corps = preparerScan(corps);
  const reponse = await appelerApi(methode, url.href, corps);
  if (methode === 'GET' && url.pathname.startsWith('/api/scan/')) suivreScan(reponse);
  if (methode === 'DELETE' && url.pathname === '/api/data' && reponse.ok) effacerTracesWeb();
  return reponse;
};

/**
 * « Effacer toutes mes données » vide IndexedDB côté server.js ; la version web
 * garde en plus la dernière adresse utilisée et les logos hors connexion.
 */
function effacerTracesWeb() {
  try { localStorage.removeItem(CLE_DERNIERE_BOITE); } catch { /* stockage refusé */ }
  const email = document.getElementById('email');
  if (email) email.value = '';
  if (!window.caches) return;
  caches.keys().then((cles) => Promise.all(cles.map(async (cle) => {
    const cache = await caches.open(cle);
    const requetes = await cache.keys();
    await Promise.all(requetes
      .filter((r) => new URL(r.url).pathname.startsWith('/api/logo/'))
      .map((r) => cache.delete(r)));
  }))).catch(() => {});
}

/** Pré-remplit la messagerie et l'adresse du dernier scan. */
function restaurerDerniereBoite() {
  let derniere;
  try { derniere = JSON.parse(localStorage.getItem(CLE_DERNIERE_BOITE) || 'null'); } catch { return; }
  if (!derniere) return;
  const select = document.getElementById('provider');
  const email = document.getElementById('email');
  if (email && !email.value) email.value = derniere.email || '';
  if (!select) return;
  const appliquer = () => {
    if (![...select.options].some((o) => o.value === derniere.fournisseur)) return false;
    select.value = derniere.fournisseur;
    select.dispatchEvent(new Event('change'));
    return true;
  };
  // La liste des messageries est remplie par app.js, après un appel à l'API.
  if (!appliquer()) {
    const observateur = new MutationObserver(() => { if (appliquer()) observateur.disconnect(); });
    observateur.observe(select, { childList: true });
  }
}

// Les liens d'export (CSV, JSON) sont de vrais liens : on intercepte le clic
// pour fabriquer le fichier ici même.
document.addEventListener('click', async (evenement) => {
  const lien = evenement.target.closest && evenement.target.closest('a[href^="/api/export"]');
  if (!lien) return;
  evenement.preventDefault();
  const reponse = await appelerApi('GET', lien.href, {});
  if (!reponse.ok) return;
  const disposition = reponse.headers.get('Content-Disposition') || '';
  const nom = (disposition.match(/filename="([^"]+)"/) || [])[1] || 'scanyt-export';
  const adresse = URL.createObjectURL(await reponse.blob());
  const telechargement = Object.assign(document.createElement('a'), { href: adresse, download: nom });
  document.body.append(telechargement);
  telechargement.click();
  telechargement.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 10000);
}, true);

// Sous le mot de passe : comment l'obtenir chez la messagerie choisie.
function afficherAide() {
  const select = document.getElementById('provider');
  const champ = document.getElementById('password');
  if (!select || !champ) return;
  const aide = document.createElement('p');
  aide.className = 'hint password-help';
  champ.closest('.field').append(aide);
  const maj = () => {
    const f = FOURNISSEURS[select.value];
    aide.replaceChildren();
    if (!f) return;
    aide.append(f.aide + ' ');
    if (f.lien) {
      const a = Object.assign(document.createElement('a'), {
        href: f.lien, target: '_blank', rel: 'noopener noreferrer', textContent: 'Y aller →'
      });
      aide.append(a);
    }
  };
  select.addEventListener('change', maj);
  // La liste est remplie par app.js après un appel à l'API.
  new MutationObserver(maj).observe(select, { childList: true });
  maj();
}

// Mode hors connexion : le site reste ouvrable sans réseau (public/sw.js).
if ('serviceWorker' in navigator && (location.protocol === 'https:' || location.hostname === 'localhost')) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

// Démarrage une fois tous les scripts de la page exécutés. Sur le site, ils sont
// chargés juste après le premier affichage (voir build.mjs) et annoncent leur
// fin par « scanyt:charge » ; chargés normalement, c'est DOMContentLoaded.
document.addEventListener(window.SCANYT_CHARGEMENT_DIFFERE ? 'scanyt:charge' : 'DOMContentLoaded', async () => {
  afficherAide();
  brancherMicrosoft();
  restaurerDerniereBoite();
  installerTutoriel();
  await pret;
  if (!persistant) {
    const note = document.querySelector('.danger-zone span');
    if (note) {
      note.textContent = 'Ce navigateur refuse le stockage local : tes résultats seront perdus '
        + 'à la fermeture de l’onglet. Pense à les exporter.';
    }
  }
});
